// Page docs (structure + permissions + flows): pages/professionals/README.md
/**
 * Professionals INDEX (rose accent) — public directory of published
 * professional profiles.
 *
 * Laid out like the organizations index (header + featured banner + filter
 * bar + card grid / list rows) but with the clean cache-first data flow of
 * the articles index: getIndexCache("professionals") hydrates first, the
 * live query is skipped while the cache is fresh, and fetches rewrite the
 * cache via setIndexCache.
 *
 * Kill switch: siteSettings.professionalsVisible === false redirects to
 * /organizations (mirrors the articles index).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { Box, Button, Container, FormControl, IconButton, InputAdornment, MenuItem, Select, Skeleton, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import NavBar from "@/components/Importable/Navigation";
import SubscriptionBanner from "@/components/Banners/SubscriptionBanner";
import SectionFooter from "@/components/CreateForm/Footer";
import { IndexPageHead } from "@/components/Importable/articles-and-regulations";
import { GridFeaturedBanner, GridFeaturedBannerSkeleton, SingleFeaturedBanner, SingleFeaturedBannerSkeleton } from "@/components/Importable/FeaturedBanner";
import ProfessionalCard from "@/components/Professionals/ProfessionalCard";
import FollowTagDialog from "@/components/Importable/FollowTagDialog";
import { useTagPreferences } from "@/hooks/useTagPreferences";
import { normalizeSearchText } from "@/utils/searchText";
import { rose, common } from "@/theme/colors";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { useDataVersionCheck } from "@/hooks/useDataVersionCheck";
import { useProgressiveLoading } from "@/hooks/useProgressiveLoading";
import { useRelevanceSplit } from "@/hooks/useRelevanceSplit";
import { openAccessGateModal } from "@/utils/accessGate";
import { getIndexCache, setIndexCache, getUserPresenceMemo, setUserPresenceMemo, clearUserPresenceMemo } from "@/utils/localStore";
import { assetUrl } from "@/utils/asset";
import { useAppDispatch } from "@/src/store/hooks";
import { api, useGetMyProfessionalQuery, useGetProfessionalsQuery, useGetPromotionsQuery } from "@/src/services/api";
import { getPlaceholderProfessionals } from "@/utils/professionalPlaceholders";
import IndexPageHeader from "@/components/Importable/IndexPageHeader";
import { useIndexLayout } from "@/hooks/useIndexLayout";
import { panelPageSx } from "@/components/Importable/IndexPanelSurface";
import { MAIN_CONTENT_ID } from "@/components/Importable/SkipLink";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const ACCENT = {
  primary: rose.primary,
  dark: rose.dark
};

/** Index card projection + the bio needed for full-text search. */

export const mapProfessionalToCard = pro => {
  const payload = pro.payload || {};
  return {
    id: pro.id,
    slug: pro.slug || payload.slug || pro.id,
    name: pro.name || payload.name || "Unnamed Professional",
    headline: pro.headline ?? payload.headline ?? undefined,
    photoUrl: payload.photoUrl ? assetUrl(payload.photoUrl) : undefined,
    location: payload.location,
    languages: Array.isArray(payload.languages) ? payload.languages : undefined,
    expertise: Array.isArray(payload.expertise) ? payload.expertise : undefined,
    bio: typeof payload.bio === "string" ? payload.bio : undefined,
    email: typeof payload.email === "string" ? payload.email : undefined
  };
};
const routeSegmentFor = pro => String(pro.slug || pro.id);

/**
 * Data layer: SSR-safe cache hydration + the live API query (skipped while a
 * fresh cache exists). Mirrors useArticlesIndexData without the local-dev
 * overlay machinery.
 */
// A professionals version bump means published profiles changed — an
// approval may have just published THIS user's profile, so the per-user
// "no profile" memo must not keep skipping /professionals/my. Clearing on
// bump over-invalidates (any user's approval clears it) but that costs one
// re-check, while a stale memo costs a wrong CTA for up to 7 days.
const clearProfileMemoOnBump = () => clearUserPresenceMemo("professional-profile");
function useProfessionalsIndexData() {
  const cacheStale = useDataVersionCheck("professionals", clearProfileMemoOnBump);
  const [cachedPros, setCachedPros] = useState(null);
  // Always start empty so the first render matches server HTML (avoids
  // hydration mismatch when in-memory cache survives HMR).
  const [professionals, setProfessionals] = useState([]);

  // Hydrate from index cache after mount (SSR-safe). Retry on next macrotask
  // in case localStore's hydration timer hadn't fired yet.
  useEffect(() => {
    const apply = () => {
      const c = getIndexCache("professionals");
      if (c?.length) {
        setCachedPros(c);
        setProfessionals(prev => prev.length === 0 ? c : prev);
        return true;
      }
      return false;
    };
    if (apply()) return;
    const t = setTimeout(apply, 0);
    return () => clearTimeout(t);
  }, []);
  const {
    data: apiProfessionals,
    isError: professionalsError
  } = useGetProfessionalsQuery(undefined, {
    skip: (cachedPros?.length ?? 0) > 0 && !cacheStale
  });

  // Replace list with API data and rewrite the cache.
  useEffect(() => {
    if (!apiProfessionals) return;
    const cards = apiProfessionals.filter(p => p.isActive && p.approvalStatus === "approved").map(mapProfessionalToCard);
    const sorted = [...cards].sort((a, b) => a.name.localeCompare(b.name));
    setProfessionals(sorted);
    setIndexCache("professionals", sorted);
  }, [apiProfessionals]);
  return {
    professionals,
    professionalsError
  };
}

/**
 * Chip rows for a featured profile card: expertise (filled), languages
 * (outlined), and the location as a single "City, Country" tag — the same
 * visual language as ProfessionalCard, in the page accent.
 */
const professionalTagGroups = pro => [{
  tags: pro.expertise ?? [],
  color: ACCENT.primary,
  colorDark: ACCENT.dark,
  variant: "filled"
}, {
  tags: pro.languages ?? [],
  color: ACCENT.primary,
  colorDark: ACCENT.dark
}, {
  tags: [[pro.location?.city, pro.location?.country].filter(Boolean).join(", ")].filter(Boolean),
  color: ACCENT.primary,
  colorDark: ACCENT.dark
}];

/**
 * "Featured Profiles" — mirrors FeaturedOrganizationSection, driven by the
 * "featured_professional" promotions placement. Hidden when no promotions
 * resolve to a loaded profile.
 */
export const FeaturedProfessionalsSection = ({
  professionals,
  onVisit
}) => {
  const dispatch = useAppDispatch();
  const {
    data: featuredPromos,
    isLoading: promosLoading
  } = useGetPromotionsQuery("featured_professional");

  // Refetch promotions when the server bumps the "promotions" data version.
  useDataVersionCheck("promotions", useCallback(() => {
    dispatch(api.util.invalidateTags(["Promotions"]));
  }, [dispatch]));
  const storedBannerType = featuredPromos?.[0]?.bannerType ?? "grid";
  const promoImageByTargetId = useMemo(() => {
    const map = {};
    for (const promo of featuredPromos ?? []) {
      map[promo.targetId] = promo.imageUrl;
    }
    return map;
  }, [featuredPromos]);
  const featuredList = useMemo(() => {
    if (!featuredPromos || featuredPromos.length === 0) return [];
    return featuredPromos.map(promo => professionals.find(p => p.id === promo.targetId || p.slug === promo.targetId)).filter(Boolean);
  }, [professionals, featuredPromos]);
  const gridItems = useMemo(() => featuredList.map(pro => ({
    id: pro.id,
    slug: pro.slug,
    name: pro.name,
    description: pro.headline || pro.bio || "",
    logoUrl: pro.photoUrl,
    city: pro.location?.city,
    tagGroups: professionalTagGroups(pro)
  })), [featuredList]);
  const singleItems = useMemo(() => featuredList.map(pro => {
    const promoImage = promoImageByTargetId[pro.id] ?? promoImageByTargetId[pro.slug];
    return {
      id: pro.id || pro.slug || pro.name,
      image: promoImage || pro.photoUrl || "",
      name: pro.name,
      description: pro.headline || pro.bio || "",
      link: `/professionals/${routeSegmentFor(pro)}`,
      tagGroups: professionalTagGroups(pro)
    };
  }), [featuredList, promoImageByTargetId]);

  // Grid needs at least 3 promoted items to fill its slots — with fewer,
  // fall back to the single rotating banner automatically.
  const bannerType = storedBannerType === "grid" && featuredList.length < 3 ? "single" : storedBannerType;
  if (featuredList.length === 0) {
    if (promosLoading) {
      return bannerType === "single" ? /*#__PURE__*/_jsx(SingleFeaturedBannerSkeleton, {
        accentColor: ACCENT.primary
      }) : /*#__PURE__*/_jsx(GridFeaturedBannerSkeleton, {
        accentColor: ACCENT.primary,
        dataSelectTarget: "Featured Profiles"
      });
    }
    return null;
  }
  const resolveOriginal = (id, slug) => featuredList.find(p => p.id === id || p.slug === (slug ?? id)) ?? featuredList[0];
  if (bannerType === "single") {
    return /*#__PURE__*/_jsx(SingleFeaturedBanner, {
      items: singleItems,
      onCta: item => onVisit(resolveOriginal(item.id)),
      sectionLabel: "Featured Profile",
      ctaLabel: "View Profile",
      accentColor: ACCENT.primary,
      accentDark: ACCENT.dark,
      dataSelectTarget: "Featured Profiles"
    });
  }
  return /*#__PURE__*/_jsx(GridFeaturedBanner, {
    list: gridItems,
    onVisit: item => onVisit(resolveOriginal(item.id, item.slug)),
    sectionLabel: "Featured Profile",
    ctaLabel: name => `View ${name}`,
    accentColor: ACCENT.primary,
    accentDark: ACCENT.dark,
    dataSelectTarget: "Featured Profiles"
  });
};

/** Inline skeleton placeholder matching the grid card footprint. */
const ProfessionalCardSkeleton = () => /*#__PURE__*/_jsxs(Box, {
  sx: {
    width: "100%",
    p: 3,
    borderRadius: 1,
    boxShadow: 2,
    backgroundColor: "background.paper",
    display: "flex",
    flexDirection: "column",
    gap: 1.25
  },
  children: [/*#__PURE__*/_jsxs(Box, {
    sx: {
      display: "flex",
      alignItems: "center",
      gap: 2
    },
    children: [/*#__PURE__*/_jsx(Skeleton, {
      variant: "circular",
      width: 72,
      height: 72
    }), /*#__PURE__*/_jsx(Skeleton, {
      variant: "text",
      width: "60%",
      height: 28
    })]
  }), /*#__PURE__*/_jsx(Skeleton, {
    variant: "text",
    width: "90%"
  }), /*#__PURE__*/_jsx(Skeleton, {
    variant: "text",
    width: "55%"
  }), /*#__PURE__*/_jsxs(Box, {
    sx: {
      display: "flex",
      gap: 0.75
    },
    children: [/*#__PURE__*/_jsx(Skeleton, {
      variant: "rounded",
      width: 72,
      height: 24
    }), /*#__PURE__*/_jsx(Skeleton, {
      variant: "rounded",
      width: 88,
      height: 24
    })]
  })]
});
const ProfessionalsIndexPage = () => {
  const router = useRouter();
  const {
    settings: siteSettings
  } = useSiteSettings();
  const {
    isAuthenticated,
    user
  } = useAuth();

  // ── Kill switch ──
  useEffect(() => {
    if (!siteSettings.professionalsVisible) void router.replace("/organizations");
  }, [siteSettings.professionalsVisible, router]);
  const {
    professionals,
    professionalsError
  } = useProfessionalsIndexData();

  // ── Followed-tag chips (expertise) ──
  // Expertise chips fill according to the viewer's followed disciplines;
  // clicking one opens a follow/unfollow confirmation.
  const {
    prefs: tagPrefs,
    toggle: toggleTag
  } = useTagPreferences();
  const [followTag, setFollowTag] = useState(null);
  const [tagSnack, setTagSnack] = useState(null);
  const followTagFollowing = !!followTag && tagPrefs.disciplines.includes(followTag);
  const confirmFollowTag = useCallback(() => {
    if (!followTag) return;
    const wasFollowing = tagPrefs.disciplines.includes(followTag);
    toggleTag("disciplines", followTag);
    setTagSnack(wasFollowing ? `"${followTag}" tag following toggled off` : `"${followTag}" tag following toggled on`);
    setFollowTag(null);
  }, [followTag, tagPrefs.disciplines, toggleTag]);

  // ── Placeholder profiles (admin Visibility toggle) ──
  // While the "Professionals placeholders" site setting is on, 20 sample
  // profiles render directly in the directory — search + all filters
  // operate on the merged list, so the dropdowns pick up the sample values
  // too. Real profiles always win on slug collision.
  const placeholdersEnabled = siteSettings.professionalsPlaceholdersEnabled;
  const placeholderCards = useMemo(() => getPlaceholderProfessionals().map(p => ({
    id: p.id,
    slug: p.slug,
    name: p.name,
    headline: p.headline,
    photoUrl: p.photoUrl,
    location: p.location,
    languages: p.languages,
    expertise: p.expertise,
    bio: p.bio
  })), []);
  const displayProfessionals = useMemo(() => {
    if (!placeholdersEnabled) return professionals;
    const realSlugs = new Set(professionals.map(p => p.slug));
    const extras = placeholderCards.filter(p => !realSlugs.has(p.slug));
    return [...professionals, ...extras].sort((a, b) => a.name.localeCompare(b.name));
  }, [professionals, placeholderCards, placeholdersEnabled]);

  // ── "Publish My Profile" visibility ──
  // Shown for guests, and for signed-in users whose profile is still
  // private (GET /professionals/my → 404 / no data). Hidden once they
  // already have a published professional page.
  //
  // A settled answer is memoised per user so repeat visits skip /my
  // entirely; createProfessional clears the memo, and its 7-day TTL heals
  // cross-device drift. A stale "no profile" memo is safe: the create
  // page prefills from /my and re-submitting acts as an edit.
  const profileMemo = useMemo(() => user?.id ? getUserPresenceMemo("professional-profile", user.id) : null, [user?.id]);
  const {
    data: myProfessional,
    isError: myProfessionalCheckFailed,
    isLoading: myProfessionalLoading,
    isSuccess: myProfessionalSettled
  } = useGetMyProfessionalQuery(undefined, {
    skip: !isAuthenticated || profileMemo !== null
  });
  useEffect(() => {
    if (!user?.id || !myProfessionalSettled) return;
    setUserPresenceMemo("professional-profile", user.id, myProfessional != null);
  }, [myProfessionalSettled, myProfessional, user?.id]);
  // 404 resolves to data:null (success) — only a settled "no profile"
  // shows the CTA. On real fetch failures we HIDE it: showing it to a user
  // who already has a page could lead to a duplicate-slug resubmission.
  const showPublishButton = !isAuthenticated || profileMemo === false || profileMemo === null && !myProfessionalLoading && !myProfessionalCheckFailed && myProfessional == null;
  const handlePublishClick = useCallback(() => {
    if (!isAuthenticated) {
      openAccessGateModal({
        reason: "login-required",
        returnUrl: "/professionals/create"
      });
      return;
    }
    router.push("/professionals/create");
  }, [isAuthenticated, router]);
  const handleProfessionalClick = useCallback(pro => {
    router.push(`/professionals/${routeSegmentFor(pro)}`);
  }, [router]);

  // ── Filter state ──
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [filterLanguage, setFilterLanguage] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterExpertise, setFilterExpertise] = useState("");

  // Dropdown options derived from the displayed profiles' distinct values
  // (includes rendered placeholders, so their values populate the filters).
  const {
    languageOptions,
    countryOptions,
    expertiseOptions
  } = useMemo(() => {
    const languages = new Set();
    const countries = new Set();
    const expertise = new Set();
    displayProfessionals.forEach(pro => {
      (pro.languages || []).forEach(l => {
        if (l && l.trim()) languages.add(l.trim());
      });
      const country = pro.location?.country?.trim();
      if (country) countries.add(country);
      (pro.expertise || []).forEach(e => {
        if (e && e.trim()) expertise.add(e.trim());
      });
    });
    const sortAll = set => Array.from(set).sort((a, b) => a.localeCompare(b));
    return {
      languageOptions: sortAll(languages),
      countryOptions: sortAll(countries),
      expertiseOptions: sortAll(expertise)
    };
  }, [displayProfessionals]);

  // ── Compose filters (AND) ──
  const filteredProfessionals = useMemo(() => {
    const term = normalizeSearchText(searchQuery.trim());
    return displayProfessionals.filter(pro => {
      if (term) {
        const haystack = normalizeSearchText([pro.name, pro.headline, pro.bio].filter(Boolean).join(" "));
        if (!haystack.includes(term)) return false;
      }
      if (filterLanguage) {
        const matches = (pro.languages || []).some(l => l === filterLanguage);
        if (!matches) return false;
      }
      if (filterCountry) {
        if ((pro.location?.country || "").trim() !== filterCountry) return false;
      }
      if (filterExpertise) {
        const matches = (pro.expertise || []).some(e => e === filterExpertise);
        if (!matches) return false;
      }
      return true;
    });
  }, [displayProfessionals, searchQuery, filterLanguage, filterCountry, filterExpertise]);

  // ── "Most relevant to you" (followed tags from the profile modal) ──
  // Professionals' expertise chips mirror the discipline tags a signed-in
  // user can follow, so relevance = expertise ∩ followed disciplines.
  //
  // Split the FILTERED set, not the raw one: searching or filtering must
  // narrow this section too, otherwise a search returns matches in the
  // list while the relevance section above it still shows non-matching
  // profiles.
  const {
    relevant: relevantProfessionals,
    hasPrefs: hasFollowedTags
  } = useRelevanceSplit(filteredProfessionals, pro => ({
    disciplines: pro.expertise ?? [],
    products: [],
    regions: []
  }));
  const hasActiveFilters = Boolean(searchQuery.trim() || filterLanguage || filterCountry || filterExpertise);
  const {
    visibleItems: visibleProfessionals,
    showSkeletons,
    skeletonCount
  } = useProgressiveLoading(filteredProfessionals, 10, professionalsError);
  const renderSelect = (label, value, onChange, options) => /*#__PURE__*/_jsx(FormControl, {
    size: "small",
    sx: {
      minWidth: 160
    },
    children: /*#__PURE__*/_jsxs(Select, {
      displayEmpty: true,
      value: value,
      onChange: e => onChange(e.target.value),
      inputProps: {
        "aria-label": `Filter by ${label}`
      },
      children: [/*#__PURE__*/_jsxs(MenuItem, {
        value: "",
        children: ["All ", label, "s"]
      }), options.map(option => /*#__PURE__*/_jsx(MenuItem, {
        value: option,
        children: option
      }, option))]
    })
  });
  const indexLayout = useIndexLayout("professionals");
  const {
    data: featuredProPromos
  } = useGetPromotionsQuery("featured_professional");
  const hasFeaturedProBanner = indexLayout.showFeatured && (featuredProPromos?.length ?? 0) > 0;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(IndexPageHead, {
      title: "Professionals Directory | Your Company",
      description: "Meet compliance and product certification professionals. Browse profiles by expertise, language, and location.",
      keywords: "professionals, consultants, compliance experts, product certification",
      canonicalUrl: "https://yourdomain.com/professionals"
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        display: "flex",
        minHeight: "100vh",
        overflowX: "hidden",
        width: "100%",
        maxWidth: "100vw",
        ...panelPageSx(indexLayout.style, ACCENT.primary)
      },
      children: /*#__PURE__*/_jsxs(Box, {
        sx: {
          flexGrow: 1,
          overflowX: "hidden",
          maxWidth: "100%",
          width: "100%"
        },
        children: [/*#__PURE__*/_jsx(NavBar, {}), indexLayout.style === 3 && /*#__PURE__*/_jsxs(_Fragment, {
          children: [indexLayout.showFeatured && /*#__PURE__*/_jsx(FeaturedProfessionalsSection, {
            professionals: professionals,
            onVisit: handleProfessionalClick
          }), !hasFeaturedProBanner && /*#__PURE__*/_jsx(IndexPageHeader, {
            style: 3,
            accent: {
              primary: ACCENT.primary,
              dark: ACCENT.dark
            },
            title: "Professionals Directory",
            subtitle: "Meet the consultants, engineers, and compliance experts on product certification \u2014 and connect with the right specialist for your project.",
            ariaLabel: "Professionals Directory Header",
            create: showPublishButton ? {
              label: "Publish My Profile",
              onClick: handlePublishClick
            } : undefined
          })]
        }), /*#__PURE__*/_jsxs(Container, {
          component: "main",
          id: MAIN_CONTENT_ID,
          tabIndex: -1,
          maxWidth: "lg",
          sx: {
            py: 4,
            px: 4
          },
          children: [indexLayout.style !== 3 && /*#__PURE__*/_jsx(IndexPageHeader, {
            style: indexLayout.style,
            accent: {
              primary: ACCENT.primary,
              dark: ACCENT.dark
            },
            title: "Professionals Directory",
            subtitle: "Meet the consultants, engineers, and compliance experts on product certification \u2014 and connect with the right specialist for your project.",
            ariaLabel: "Professionals Directory Header",
            create: showPublishButton ? {
              label: "Publish My Profile",
              onClick: handlePublishClick
            } : undefined
          }), indexLayout.style === 3 && hasFeaturedProBanner && /*#__PURE__*/_jsx(IndexPageHeader, {
            style: 1,
            accent: {
              primary: ACCENT.primary,
              dark: ACCENT.dark
            },
            title: "Professionals Directory",
            subtitle: "Meet the consultants, engineers, and compliance experts on product certification \u2014 and connect with the right specialist for your project.",
            ariaLabel: "Professionals Directory Header",
            create: showPublishButton ? {
              label: "Publish My Profile",
              onClick: handlePublishClick
            } : undefined
          }), indexLayout.style !== 3 && indexLayout.showFeatured && /*#__PURE__*/_jsx(FeaturedProfessionalsSection, {
            professionals: professionals,
            onVisit: handleProfessionalClick
          }), /*#__PURE__*/_jsxs(Box, {
            "data-select-target": "Professional Filters",
            sx: {
              mt: 4,
              mb: 3,
              p: 2,
              backgroundColor: common.white,
              borderRadius: 2,
              boxShadow: 1,
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap"
            },
            children: [/*#__PURE__*/_jsx(TextField, {
              size: "small",
              placeholder: "Search professionals...",
              value: searchQuery,
              onChange: e => setSearchQuery(e.target.value),
              sx: {
                minWidth: 220,
                flex: "1 1 220px"
              },
              InputProps: {
                startAdornment: /*#__PURE__*/_jsx(InputAdornment, {
                  position: "start",
                  children: /*#__PURE__*/_jsx(SearchIcon, {
                    sx: {
                      color: ACCENT.primary
                    }
                  })
                })
              },
              inputProps: {
                "aria-label": "Search professionals"
              }
            }), /*#__PURE__*/_jsx(Tooltip, {
              title: viewMode === "grid" ? "Switch to list view" : "Switch to grid view",
              placement: "top",
              arrow: true,
              children: /*#__PURE__*/_jsx(IconButton, {
                "aria-label": viewMode === "grid" ? "Switch to list view" : "Switch to grid view",
                onClick: () => setViewMode(viewMode === "grid" ? "list" : "grid"),
                sx: {
                  border: `1px solid ${alpha(ACCENT.primary, 0.5)}`,
                  borderRadius: 1,
                  color: ACCENT.primary,
                  "&:hover": {
                    backgroundColor: alpha(ACCENT.primary, 0.08)
                  }
                },
                children: viewMode === "grid" ? /*#__PURE__*/_jsx(ViewListIcon, {}) : /*#__PURE__*/_jsx(ViewModuleIcon, {})
              })
            }), renderSelect("Language", filterLanguage, setFilterLanguage, languageOptions), renderSelect("Location", filterCountry, setFilterCountry, countryOptions), renderSelect("Expertise", filterExpertise, setFilterExpertise, expertiseOptions)]
          }), isAuthenticated && hasFollowedTags && relevantProfessionals.length > 0 && /*#__PURE__*/_jsxs(Box, {
            "aria-label": "Most Relevant Professionals",
            "data-select-target": "Most Relevant Professionals",
            sx: {
              mt: 4,
              borderRadius: 2,
              // Professionals rose: solid border,
              // same hue at 0.3 alpha for the fill.
              backgroundColor: alpha(rose.primary, 0.3),
              border: "1px solid",
              borderColor: rose.primary,
              p: {
                xs: 2,
                md: 3
              }
            },
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "h6",
              sx: {
                fontWeight: 600,
                mb: 2
              },
              children: "Most relevant to you"
            }), /*#__PURE__*/_jsx(Box, {
              sx: {
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fill, minmax(300px, 1fr))",
                  md: "repeat(auto-fill, minmax(320px, 1fr))"
                },
                gap: 3
              },
              children: relevantProfessionals.map(pro => /*#__PURE__*/_jsx(ProfessionalCard, {
                professional: pro,
                variant: "grid",
                accentColor: ACCENT.primary,
                onClick: () => handleProfessionalClick(pro),
                followedTags: tagPrefs.disciplines,
                onTagClick: setFollowTag
              }, `rel-${routeSegmentFor(pro)}`))
            })]
          }), /*#__PURE__*/_jsx(Box, {
            "aria-label": "Professionals List",
            "data-select-target": "Professionals List"
            // mt clears the relevance panel's border, which
            // otherwise sat flush against the first result.
            ,
            sx: {
              mt: 4,
              pb: 4
            },
            children: showSkeletons ? /*#__PURE__*/_jsx(Box, {
              sx: {
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fill, minmax(300px, 1fr))",
                  md: "repeat(auto-fill, minmax(320px, 1fr))"
                },
                gap: 3
              },
              children: Array.from({
                length: skeletonCount
              }).map((_, i) => /*#__PURE__*/_jsx(ProfessionalCardSkeleton, {}, `skel-${i}`))
            }) : filteredProfessionals.length === 0 ? /*#__PURE__*/_jsxs(Box, {
              sx: {
                textAlign: "center",
                py: 8,
                px: 3,
                backgroundColor: common.white,
                borderRadius: 2,
                boxShadow: 1
              },
              children: [/*#__PURE__*/_jsx(Typography, {
                variant: "h5",
                color: "text.secondary",
                gutterBottom: true,
                children: hasActiveFilters && professionals.length > 0 ? "No profiles match your filters" : "No published profiles yet"
              }), /*#__PURE__*/_jsx(Typography, {
                variant: "body1",
                color: "text.secondary",
                paragraph: true,
                children: hasActiveFilters && professionals.length > 0 ? "Try adjusting your search or clearing a filter." : "Be the first to share your expertise with the community."
              }), showPublishButton && !(hasActiveFilters && professionals.length > 0) && /*#__PURE__*/_jsx(Button, {
                variant: "contained",
                onClick: handlePublishClick,
                sx: {
                  bgcolor: ACCENT.primary,
                  "&:hover": {
                    bgcolor: ACCENT.dark
                  }
                },
                children: "Publish My Profile"
              })]
            }) : viewMode === "grid" ? /*#__PURE__*/_jsx(Box, {
              sx: {
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fill, minmax(300px, 1fr))",
                  md: "repeat(auto-fill, minmax(320px, 1fr))"
                },
                gap: 3
              },
              children: visibleProfessionals.map(pro => /*#__PURE__*/_jsx(ProfessionalCard, {
                professional: pro,
                variant: "grid",
                accentColor: ACCENT.primary,
                onClick: () => handleProfessionalClick(pro),
                followedTags: tagPrefs.disciplines,
                onTagClick: setFollowTag
              }, routeSegmentFor(pro)))
            }) : /*#__PURE__*/_jsx(Box, {
              sx: {
                display: "flex",
                flexDirection: "column",
                gap: 1.5
              },
              children: visibleProfessionals.map(pro => /*#__PURE__*/_jsx(ProfessionalCard, {
                professional: pro,
                variant: "list",
                accentColor: ACCENT.primary,
                onClick: () => handleProfessionalClick(pro),
                followedTags: tagPrefs.disciplines,
                onTagClick: setFollowTag
              }, routeSegmentFor(pro)))
            })
          })]
        }), /*#__PURE__*/_jsx(SubscriptionBanner, {}), /*#__PURE__*/_jsx(SectionFooter, {}), /*#__PURE__*/_jsx(FollowTagDialog, {
          tag: followTag,
          following: followTagFollowing,
          accentColor: ACCENT.primary,
          accentDark: ACCENT.dark,
          onConfirm: confirmFollowTag,
          onClose: () => setFollowTag(null)
        }), /*#__PURE__*/_jsx(Snackbar, {
          open: !!tagSnack,
          autoHideDuration: 2500,
          onClose: () => setTagSnack(null),
          message: tagSnack ?? "",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center"
          }
        })]
      })
    })]
  });
};
export default ProfessionalsIndexPage;